import React from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
  Collapse,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import { alpha } from "@mui/material/styles";

const SGMFilters = ({
  selectedMarkets,
  selectedBookies,
  maxOdds,
  minOdds,
  minBookieCount,
  fairOddsOnly,
  sortedMarkets,
  sortedBookies,
  handleMarketChange,
  handleBookieChange,
  handleMaxOddsChange,
  handleMinOddsChange,
  handleMinBookieCountChange,
  handleFairOddsToggle,
  clearFilters,
  activeFilterCount,
  filteredRows,
  processedBets,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filtersOpen, setFiltersOpen] = React.useState(!isMobile);

  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        width: isMobile ? "100%" : "600px",
        flexGrow: 0,
        flexShrink: 0,
        position: "relative",
      }}
    >
      {/* Filters header with toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: filtersOpen ? 2 : 0,
          cursor: "pointer",
        }}
        onClick={toggleFilters}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FilterAltIcon
            sx={{
              mr: 1,
              color: activeFilterCount ? "primary.main" : "inherit",
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Filters
          </Typography>
          {activeFilterCount > 0 && (
            <Chip
              size="small"
              label={activeFilterCount}
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={filtersOpen}>
        <Box sx={{ mt: 1 }}>
          {/* Results counter */}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 2, fontStyle: "italic" }}
          >
            Showing {filteredRows.length} of {processedBets.length} total bets
          </Typography>

          <Grid container spacing={2}>
            {/* Markets filter */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="market-filter-label">Markets</InputLabel>
                <Select
                  labelId="market-filter-label"
                  id="market-filter"
                  multiple
                  value={selectedMarkets}
                  onChange={handleMarketChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  label="Markets"
                >
                  {sortedMarkets.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Bookies filter */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="bookie-filter-label">Bookies</InputLabel>
                <Select
                  labelId="bookie-filter-label"
                  id="bookie-filter"
                  multiple
                  value={selectedBookies}
                  onChange={handleBookieChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  label="Bookies"
                >
                  {sortedBookies.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Min Odds, Max Odds, and Min Bookie Count in one row */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    label="Min Odds"
                    value={minOdds}
                    onChange={handleMinOddsChange}
                    size="small"
                    type="number"
                    inputProps={{ min: "1.01", step: "0.1" }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Max Odds"
                    value={maxOdds}
                    onChange={handleMaxOddsChange}
                    size="small"
                    type="number"
                    inputProps={{ min: "1", step: "0.1" }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    label="Min Count"
                    value={minBookieCount}
                    onChange={handleMinBookieCountChange}
                    size="small"
                    type="number"
                    inputProps={{ min: "1", step: "1" }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fairOddsOnly}
                        onChange={handleFairOddsToggle}
                        color="primary"
                      />
                    }
                    label="Fair Odds"
                    sx={{ whiteSpace: "nowrap" }}
                  />
                </Grid>
              </Grid>

              {/* Clear filters button - only shown if filters active */}
            </Grid>
          </Grid>
          {activeFilterCount > 0 && (
            <Box sx={{ ml: 2 }}>
              <Chip
                label="Clear"
                onDelete={clearFilters}
                onClick={clearFilters}
                deleteIcon={<ClearIcon />}
                variant="outlined"
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                  position: "absolute",
                  top: "16px",
                  right: "64px",
                }}
              />
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SGMFilters;
